import {Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {MatTableDataSource} from "@angular/material/table";
import {MatDialog} from "@angular/material/dialog";
import {
  AnalysisRequiredDialogComponent
} from "../../dialogs/lab-manager/analysis-required-dialog/analysis-required-dialog.component";
import {
  DraftConfirmationDialogComponent
} from "../../dialogs/draft-confirmation-dialog/draft-confirmation-dialog.component";
import {BasicLabManagerItemModel} from "../../models/lab-manager/basic-lab-manager-item";
import {LabManagerSharedDataService} from "../lab-manager-shared-data.service";
import {LabManagerService} from "../lab-manager.service";
import {NavBarComponent} from "../../nav-bar/nav-bar.component";
import {LabSubmissionDetailsModel} from "../../models/lab-manager/lab-submission-details";

@Component({
    selector: 'app-lab-analysis-required',
    templateUrl: './lab-analysis-required.component.html',
    styleUrls: ['./lab-analysis-required.component.css'],
    standalone: false
})
export class LabAnalysisRequiredComponent implements OnInit {

  labSubmissionDetails: LabSubmissionDetailsModel = null;
  analysisTypes: BasicLabManagerItemModel[] = [];
  currentPage: number = 0;
  dataSource: MatTableDataSource<any> = null;
  /*
   * Rebuilt from this, never mutated. See the subscription in the constructor.
   */
  private static readonly ALL_COLUMNS: readonly string[] = [
    'checkbox',
    'farm',
    'field',
    'sample',
    'analysisRequired'
  ];
  displayedColumns: string[] = [...LabAnalysisRequiredComponent.ALL_COLUMNS];
  pageSize: number = 9999;
  selectedSamples: number[] = [];
  selectedAnalysisTypes: number[] = [];
  tableHeight: string = (window.innerHeight - 500) + 'px';
  totalElements: number = 0;
  searchText: string = '';
  timerInterval: any = null;
  mergeGroups: any[] = [];
  @Output() analysisChange: EventEmitter<any> = new EventEmitter<any>();

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.tableHeight = (window.innerHeight - 500) + 'px';
  }

  constructor(
      private dialog: MatDialog,
      private labManagerSharedDataService: LabManagerSharedDataService,
      private labManagerService: LabManagerService,
      private nav: NavBarComponent
  ) {
    labManagerSharedDataService.labAnalysisType$.pipe(takeUntilDestroyed()).subscribe((res: BasicLabManagerItemModel[]) => {
      this.analysisTypes = res;
    });

    labManagerSharedDataService.labSubmissionDetailsData$.pipe(takeUntilDestroyed()).subscribe(res => {
      /*
       * GUARD FIRST, ASSIGN SECOND. This used to assign and then bail, so a null
       * emptied the tab the user was looking at. The other three consumers of this
       * subject already keep what they have; this one was the odd one out, and it
       * is why the analysis tab blanked in place rather than only on re-entry.
       */
      if (!res?.id) return;
      this.labSubmissionDetails = res;
      this.getLabAnalysisList();
      if (res.labManLabId && res.labManTypesId) {
        this.getAnalysisTypes();
      }
      this.loadMergeGroups();

      /*
       * Rebuilt, not shifted. This subject emits more than once per submission, and
       * `shift()` mutates the array in place, so the second emission took Farm off
       * the table, the third took Field, and so on until the grid was down to its
       * last column. Selection is hidden once the submission is past draft, which
       * is all the original line was trying to say.
       */
      this.displayedColumns = res.labManStatusId >= 2
        ? LabAnalysisRequiredComponent.ALL_COLUMNS.filter(column => column !== 'checkbox')
        : [...LabAnalysisRequiredComponent.ALL_COLUMNS];
    });
  }

  ngOnInit(): void {
  }

  setAnalysisDropDown() {
    if(this.selectedSamples.length === 1) {
      let sample = this.dataSource.data.find(s => s.id === this.selectedSamples[0]);
      this.selectedAnalysisTypes = sample.analysisRequired.map(a => a.labManItemId);
    }
  }

  getAnalysisTypes() {
    this.labManagerService.getAnalysisItems(this.labSubmissionDetails.labManLabId, this.labSubmissionDetails.labManTypesId).subscribe((res: BasicLabManagerItemModel[]) => {
      this.analysisTypes = res;
    });
  }

  getLabAnalysisList() {
    this.analysisChange.emit();
    if(this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.nav.toggleLoading(true);
    this.labManagerService.getLabAnalysisList(this.labSubmissionDetails.id.toString(), this.currentPage, this.pageSize, this.searchText).subscribe({
      next: (res) => {
        this.dataSource = new MatTableDataSource<any>(res.content)
        this.totalElements = res.totalElements;
        this.nav.toggleLoading(false);
      },
      error: () => {
        this.nav.toggleLoading(false);
      }
    })
  }

  addAnalysis() {
    const dialogRef = this.dialog.open(AnalysisRequiredDialogComponent, {
      data: {
        dialogTitle: 'ADD ANALYSIS',
        dialogType: 'add',
        selectedLength: this.selectedSamples.length,
        totalSamples: this.totalElements
      },
      width: '420px'
    });


    dialogRef.afterClosed().subscribe((res) => {
      if(res) {
        this.nav.toggleLoading(true);

        if(res === 1) {
          let analysisBody = [];

          for(let sample of this.selectedSamples) {
            analysisBody.push({
              id: sample,
              itemIds: this.selectedAnalysisTypes
            });

          }

          this.labManagerService.addLabAnalysis(analysisBody).subscribe({
            next: () => {
              this.nav.toggleLoading(false);
              this.selectedSamples = [];
              this.selectedAnalysisTypes = [];
              this.getLabAnalysisList();
            },
            error: () => {
              this.nav.toggleLoading(false);
              this.selectedSamples = [];
              this.selectedAnalysisTypes = [];
            }
          })
        } else {
          let analysisBody = {
            id: this.labSubmissionDetails.id,
            itemIds: this.selectedAnalysisTypes
          }
          this.labManagerService.addToAllLabAnalyses(analysisBody).subscribe({
            next: () => {
              this.nav.toggleLoading(false);
              this.getLabAnalysisList();
              this.selectedSamples = [];
              this.selectedAnalysisTypes = [];
            },
            error: () => {
              this.nav.toggleLoading(false);
            }
          })
        }
      }
    })
  }

  searchLabAnalysis(searchText: string) {
    if(this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.timerInterval = setInterval(() => {
      this.searchText = searchText;
      this.getLabAnalysisList();
    }, 1000);
  }

  clearAnalysis() {
    const dialogRef = this.dialog.open(AnalysisRequiredDialogComponent, {
      data: {
        dialogTitle: 'CLEAR ANALYSIS',
        dialogType: 'clear',
        selectedLength: this.selectedSamples.length,
        totalSamples: this.totalElements
      },
      width: '420px'
    });

    dialogRef.afterClosed().subscribe({
      next: (res:any) => {
        if(res) {
          this.nav.toggleLoading(true);

          if(res === 1) {
            let count = 0;

            for(let sample of this.selectedSamples) {
              this.labManagerService.removeLabAnalysis(sample).subscribe({
                next: () => {
                  count++;
                  if(count === this.selectedSamples.length) {
                    this.nav.toggleLoading(false);
                    this.getLabAnalysisList();
                    this.selectedSamples = [];
                    this.selectedAnalysisTypes = [];
                  }
                },
                error: () => {
                  count++;
                  if(count === this.selectedSamples.length) {
                    this.nav.toggleLoading(false);
                    this.selectedSamples = [];
                    this.selectedAnalysisTypes = [];
                  }
                }
              })
            }
          } else {
            this.labManagerService.removeAllLabAnalyses(this.labSubmissionDetails.id).subscribe({
              error: () => {
                this.nav.toggleLoading(false);
                this.selectedSamples = [];
                this.selectedAnalysisTypes = [];
              },
              next: () => {
                this.nav.toggleLoading(false);
                this.getLabAnalysisList();
                this.selectedSamples = [];
                this.selectedAnalysisTypes = [];
              }
            })
          }
        }
      }
    })
  }

  removeSample(sampleId: number, analysis: string) {
    const dialogRef = this.dialog.open(DraftConfirmationDialogComponent, {
      data: {
        dialogType: 'remove',
        dialogTitle: 'REMOVE ANALYSIS',
        dialogSubTitle: 'Are you sure you want to remove this Analysis?'
      }
    })
  }

  onPaginateChange(event: any) {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.getLabAnalysisList();
  }
  
  selectAllSamples(event: any): void {
    this.dataSource.data.forEach(item => {
      this.selectSample(event, item.id);
    });

  }
  
  selectSample(event: any, id:number) {

    if (event.checked) {
      // Add to the array if checked
      if(!this.selectedSamples.includes(id)) {
        this.selectedSamples.push(id);
      }
    } else {
      // Remove from the array if unchecked
      const index = this.selectedSamples.indexOf(id);
      if (index > -1) {
        this.selectedSamples.splice(index, 1);
      }
    }
  }

  loadMergeGroups() {
    if (this.labSubmissionDetails?.id) {
      this.labManagerService.getMergeGroups(this.labSubmissionDetails.id).subscribe({
        next: (groups) => this.mergeGroups = groups || [],
        error: () => this.mergeGroups = []
      });
    }
  }

  getMergeCount(sampleId: number): number {
    const group = this.mergeGroups.find(g => g.primarySampleId === sampleId);
    return group ? group.linkedCount + 1 : 0;
  }
}
